import { useState } from "react";
import { toast } from "sonner";
import { Check, AlertTriangle, CheckCircle2, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChecklistList } from "@/components/ChecklistList";
import { EquipmentEditor, toPayload } from "@/components/EquipmentLines";
import { EvidencePanel } from "@/components/EvidencePanel";
import { RESULT } from "@/components/wo/ExtraServices";
import { TechComments } from "@/components/wo/TechComments";
import { api, errMsg } from "@/lib/api";
import { vehicleMissing, vehicleText } from "@/lib/gps";
import { SvcAcTerminals } from "@/components/wo/AcTerminals";

const isDone = (s) => ["completada", "completada_obs"].includes(s.outcome?.result);
const useSvc = (wo, setWo, s) => (svc) => setWo((w) => ({ ...w, extra_services: (w.extra_services || []).map((x) => (x.id === s.id ? svc : x)) }));

const Row = ({ ok, label, testid }) => (
  <div className="flex items-center gap-2 text-sm" data-testid={testid}>
    {ok ? <Check className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-amber-400" />}
    <span className={ok ? "" : "text-amber-200"}>{label}</span>
  </div>
);

export const ServiceList = ({ wo, extras, testid }) => (
  <div className="space-y-2 rounded-xl border border-border bg-card p-4" data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Servicios de esta OS</div>
    <div className="text-sm" data-testid={`${testid}-1`}>1. {wo.service_type_name} · {wo.business_unit_name} <span className="text-xs text-muted-foreground">— resultado en el cierre general</span></div>
    {extras.map((s, k) => (
      <div key={s.id} className="text-sm" data-testid={`${testid}-${k + 2}`}>{k + 2}. {s.service_type_name} · {s.business_unit_name} <span className={isDone(s) ? "text-xs font-semibold text-emerald-400" : "text-xs font-semibold text-orange-400"}>— {RESULT[s.outcome?.result] || "Sin terminar"}</span></div>
    ))}
  </div>
);

export const SvcChecklist = ({ wo, setWo, s, editable, n }) => {
  const onOne = useSvc(wo, setWo, s);
  const toggle = async (item, patch = { checked: !item.checked }) => {
    try {
      const { data } = await api.patch(`/work-orders/${wo.id}/services/${s.id}/checklist/${item.id}`, patch);
      onOne({ ...s, checklist: data });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid={`svc-${n}-checklist`}>
      {s.scope && <p className="whitespace-pre-wrap text-sm">{s.scope}</p>}
      {s.notes && <p className="whitespace-pre-wrap text-xs text-muted-foreground">Observaciones para el técnico: {s.notes}</p>}
      <ChecklistList items={s.checklist?.items} onToggle={editable && !isDone(s) ? toggle : null} disabled={!editable || isDone(s)} large tri={s.checklist?.mode === "tri"} />
    </div>
  );
};

export const SvcEquipment = ({ wo, setWo, s, editable, n }) => {
  const onOne = useSvc(wo, setWo, s);
  const [lines, setLines] = useState(s.equipment || []);
  const [saving, setSaving] = useState(false);
  const ed = editable && !isDone(s);
  const dirty = JSON.stringify(toPayload(lines)) !== JSON.stringify(toPayload(s.equipment || []));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/work-orders/${wo.id}/services/${s.id}/tech`, { equipment: toPayload(lines) });
      onOne(data);
      setLines(data.equipment);
      toast.success("Equipamiento guardado");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-3" data-testid={`svc-${n}-equipment`}>
      <p className="text-sm text-muted-foreground">Registra lo que realmente se instaló, revisó o entregó en este servicio.</p>
      <EquipmentEditor lines={lines} onChange={setLines} disabled={!ed} buCode={s.business_unit_code} allowManual workOrderId={wo.id} allowNotes />
      {ed && dirty && <Button className="h-14 w-full text-base" onClick={save} disabled={saving} data-testid={`svc-${n}-equipment-save-button`}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Guardar equipamiento</Button>}
      {s.business_unit_code === "gps" && <SvcGpsData wo={wo} setWo={setWo} s={s} ed={ed} n={n} />}
      <SvcAcTerminals wo={wo} s={s} editable={ed} onSvc={onOne} n={n} />
    </div>
  );
};

const GPS_FIELDS = [["imei", "IMEI o número de serie del dispositivo"], ["location", "Ubicación del dispositivo instalado"]];

const OsVehicle = ({ wo, n }) => {
  const v = wo.extensions?.vehicle;
  const miss = vehicleMissing(v);
  return (
    <div className="rounded-md bg-muted/40 p-2 text-xs" data-testid={`svc-${n}-gps-vehicle-readonly`}>
      <div className="text-muted-foreground">Vehículo de la OS</div>
      <div className="font-medium">{vehicleText(v) || "Sin capturar"}</div>
      {miss.length > 0 && <div className="mt-1 text-amber-300">Completa en la tarjeta «Vehículo» de la OS: {miss.join(", ")}</div>}
    </div>
  );
};

const SvcGpsData = ({ wo, setWo, s, ed, n }) => {
  const onOne = useSvc(wo, setWo, s);
  const [d, setD] = useState(s.gps_data || {});
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/work-orders/${wo.id}/services/${s.id}/tech`, { gps_data: Object.fromEntries(GPS_FIELDS.map(([k]) => [k, d[k] || ""])) });
      onOne(data);
      toast.success("Datos del dispositivo GPS guardados");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-2 rounded-lg border border-border p-3" data-testid={`svc-${n}-gps-data`}>
      <div className="text-sm font-semibold">Dispositivo GPS y vehículo</div>
      <p className="text-xs text-muted-foreground">El modelo del dispositivo se registra arriba, en el equipamiento.</p>
      {wo.business_unit_code === "videovigilancia_movil" && <OsVehicle wo={wo} n={n} />}
      {GPS_FIELDS.map(([k, label]) => (
        <label key={k} className="block space-y-1"><span className="text-xs text-muted-foreground">{label}</span>
          <Input value={d[k] || ""} onChange={(e) => setD({ ...d, [k]: e.target.value })} disabled={!ed} className="h-11" data-testid={`svc-${n}-gps-${k}`} /></label>
      ))}
      {ed && <Button className="h-12 w-full" onClick={save} disabled={saving} data-testid={`svc-${n}-gps-save-button`}>Guardar datos GPS</Button>}
    </div>
  );
};

export const SvcPhotos = ({ wo, s, editable }) => <EvidencePanel woId={wo.id} serviceId={s.id} editable={editable && !isDone(s)} addOnly={wo.status === "pendiente_cierre"} />;

export const SvcNotes = ({ wo, setWo, s, editable, n }) => {
  const onOne = useSvc(wo, setWo, s);
  const [text, setText] = useState(s.technician_notes || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      onOne((await api.put(`/work-orders/${wo.id}/services/${s.id}/tech`, { technician_notes: text })).data);
      toast.success("Observaciones guardadas");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-3">
      <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} disabled={!editable || isDone(s)} className="text-base" placeholder="Observaciones generales de este servicio" data-testid={`svc-${n}-notes-input`} />
      {editable && !isDone(s) && <Button className="h-14 w-full text-base" onClick={save} disabled={saving || text === (s.technician_notes || "")} data-testid={`svc-${n}-notes-save-button`}>Guardar observaciones</Button>}
      <TechComments wo={wo} setWo={setWo} canAdd={wo.status === "pendiente_cierre"} serviceId={s.id} />
    </div>
  );
};

export const SvcFinish = ({ wo, setWo, s, editable, n, onNext }) => {
  const onOne = useSvc(wo, setWo, s);
  const [notes, setNotes] = useState(s.outcome?.notes || "");
  const missing = (s.checklist?.items || []).filter((i) => i.required && !i.checked).length;
  const finish = async (result) => {
    try {
      onOne((await api.post(`/work-orders/${wo.id}/services/${s.id}/finish`, { result, notes })).data);
      toast.success("Servicio actualizado");
      onNext();
    } catch (e) { toast.error(errMsg(e), { duration: 6000 }); }
  };
  return (
    <div className="space-y-4" data-testid={`svc-${n}-finish`}>
      <div className="space-y-2 rounded-xl border border-border bg-card p-4">
        <Row ok={isDone(s)} label={`Estado: ${RESULT[s.outcome?.result] || "Sin terminar"}`} testid={`svc-${n}-status`} />
        <Row ok={missing === 0} label={missing === 0 ? "Checklist obligatorio completo" : `${missing} puntos obligatorios sin completar`} />
        <Row ok={(s.equipment || []).length > 0} label={`${(s.equipment || []).length} partidas de equipo`} />
      </div>
      {editable && (
        <div className="space-y-2">
          <Textarea rows={2} placeholder="Observaciones o motivo del pendiente" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`svc-${n}-outcome-notes`} />
          <div className="grid gap-2">
            <Button className="h-14" onClick={() => finish("completada")} data-testid={`svc-${n}-finish-button`}><CheckCircle2 className="mr-1 h-4 w-4" />Terminar servicio</Button>
            <Button variant="outline" className="h-12" onClick={() => finish("completada_obs")} data-testid={`svc-${n}-finish-obs-button`}>Terminar con observaciones</Button>
            <Button variant="outline" className="h-12" onClick={() => finish("pendiente")} data-testid={`svc-${n}-pending-button`}>Pendiente</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const MainDone = ({ wo, photos, onNext }) => {
  const missing = wo.checklist.items.filter((i) => i.required && !i.checked).length;
  return (
    <div className="space-y-4" data-testid="svc-1-finish">
      <div className="space-y-2 rounded-xl border border-border bg-card p-4">
        <Row ok={missing === 0} label={missing === 0 ? "Checklist obligatorio completo" : `${missing} puntos obligatorios sin completar`} />
        <Row ok={wo.equipment.length > 0} label={`${wo.equipment.length} partidas de equipo`} />
        <Row ok={photos > 0} label={`${photos} fotografías de este servicio`} />
      </div>
      <p className="text-sm text-muted-foreground">El resultado de este servicio se registra en el cierre general de la OS.</p>
      <Button className="h-14 w-full text-base" onClick={onNext} data-testid="svc-1-continue-button">Continuar al siguiente servicio<ChevronRight className="ml-1 h-4 w-4" /></Button>
    </div>
  );
};
